import React, { useEffect, useState } from 'react';
import type { Course, PlanId, PricingPlan } from './CourseCard.types';
import FeaturedCard from './FeaturedCard';
import RegularCard from './RegularCard';
import styles from './CoursePricing.module.css';
import { useTranslation } from 'react-i18next';
import PaymentPopup from '../PaymentPopup/PaymentPopup';

type Pricing = { plans: Record<PlanId, PricingPlan> };

const CoursePricing: React.FC = () => {
  const { t } = useTranslation();
  const cards = t('coursePricing.cards', { returnObjects: true }) as Course[];

  const [pricing, setPricing] = useState<Pricing | null>(null);

  useEffect(() => {
    fetch('/pricing.json', { cache: 'no-store' })
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data: Pricing) => setPricing(data))
      .catch(() => setPricing(null));
  }, []);

  const [checkout, setCheckout] = useState<{
    open: boolean;
    planId?: PlanId;
    price?: PricingPlan;
    index?: number;
  }>({ open: false });

  if (!pricing) return null;

  const featuredCards = cards.filter(course => course.layout === 'featured');
  const regularCards = cards.filter(course => course.layout !== 'featured');

  return (
    <section id="course-pricing" className={styles['course-pricing__section']}>
      <h2 className={styles['course-pricing__title']}>
        {t('coursePricing.title')}
      </h2>

      <p className={styles['course-card__note']}>{t('coursePricing.note')}</p>

      <div className={styles['course-pricing__cards']}>
        {!!featuredCards.length && (
          <div className={styles['course-pricing__featured']}>
            {featuredCards.map((course: Course) => {
              const index = cards.findIndex(card => card.id === course.id);
              const price = pricing.plans[course.id as PlanId];

              return (
                <FeaturedCard
                  key={course.id}
                  course={course}
                  i18nIndex={index}
                  price={price}
                  onBuy={() =>
                    setCheckout({
                      open: true,
                      planId: course.id as PlanId,
                      price,
                      index,
                    })
                  }
                />
              );
            })}
          </div>
        )}

        <div className={styles['course-pricing__regular']}>
          {regularCards.map((course: Course) => {
            const sourceIndex = cards.findIndex(card => card.id === course.id);
            const price = pricing.plans[course.id as PlanId];

            return (
              <RegularCard
                key={course.id}
                course={course}
                i18nIndex={sourceIndex}
                price={price}
                onBuy={() =>
                  setCheckout({
                    open: true,
                    planId: course.id as PlanId,
                    price,
                    index: sourceIndex,
                  })
                }
              />
            );
          })}
        </div>
      </div>

      {checkout.open && checkout.planId && (
        <PaymentPopup
          planId={checkout.planId}
          i18nIndex={checkout.index}
          price={checkout.price}
          onClose={() => setCheckout({ open: false })}
        />
      )}
    </section>
  );
};

export default CoursePricing;